import React, { useState, useEffect, useRef } from "react";
import Image from "../image/Image";
import Button from "../button/Button";
import CancelButton from "../button/CancelButton";
import {
  addMerchandiseSales,
  getMerchandiseUsers,
} from "../../API/MerchandiseProductAPI/MerchandiseProductAPI";
import toast from "react-hot-toast";
import Loader from "../Loader/Loader";

function AddSalesForm({
  product,
  addSaleFormData,
  setAddSaleFormData,
  setShowAddSalesModal,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSelectUserOpen, setSelectUserIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const dropdownRef = useRef(null); // Ref for the dropdown
  const [loading, setLoading] = useState(false);

  const filteredUsers = users.filter((user) =>
    `${user.firstName} ${user.lastName}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSelectUserIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getMerchandiseProductsUsers = async (productId) => {
    try {
      setLoading(true);
      setSelectUserIsOpen((prev) => !prev);
      setAddSaleFormData((prev) => ({
        ...prev,
        merchandiseProductId: productId,
      }));

      if (!isSelectUserOpen) {
        const merchandiseUsers = await getMerchandiseUsers({ productId });
        setUsers(merchandiseUsers);
      } else {
        setUsers([]);
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setAddSaleFormData((prev) => ({
      ...prev,
      user: user,
    }));
    setSelectUserIsOpen(false);
  };

  const handleAddTaskChange = async () => {
    // Validate form data
    if (!addSaleFormData.user) {
      toast.error("Please select a user");
      return;
    }
    if (!addSaleFormData.totalItem || addSaleFormData.totalItem <= 0) {
      toast.error("Please enter a valid number of items sold");
      return;
    }

    try {
      await addMerchandiseSales({
        userId: addSaleFormData.user.userId,
        merchandiseProductId: addSaleFormData.merchandiseProductId,
        totalItem: addSaleFormData.totalItem,
      });
      toast.success("Sales added successfully");
      setShowAddSalesModal(null);
      // Reset form data
      setAddSaleFormData({
        user: null,
        totalItem: "",
        merchandiseProductId: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Error adding sales");
      console.error("Error adding sales:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-[100]">
      <div className="bg-white rounded-lg p-6 shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">Add Sale</h2>
        <div>
          {/* User Dropdown */}
          <div className="mb-4" ref={dropdownRef}>
            <label
              htmlFor="customDropdown"
              className="block text-sm font-medium text-gray-700"
            >
              Select User
            </label>
            <div className="relative">
              <button
                id="customDropdown"
                className="w-full flex items-center justify-between border border-gray-300 rounded-md shadow-sm px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onClick={() => getMerchandiseProductsUsers(product._id)}
              >
                <span className="text-gray-500">
                  {!addSaleFormData.user
                    ? "-- Select User --"
                    : `${addSaleFormData.user.firstName} ${addSaleFormData.user.lastName}`}
                </span>
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isSelectUserOpen && (
                <div className="absolute w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-20">
                  <input
                    type="text"
                    placeholder="Search for users..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />

                  <ul className="w-full justify-center items-center overflow-auto max-h-48">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <li
                          key={user._id}
                          onClick={() => handleUserSelect(user)}
                          className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                        >
                          <Image
                            src={user.profilePhoto}
                            alt={`${user.firstName} ${user.lastName}`}
                            className="w-8 h-8 rounded-full"
                          />
                          <div className="ml-3 flex flex-col">
                            <span className="font-medium text-gray-800 text-sm">
                              {user.firstName} {user.lastName}
                            </span>
                            <span className="text-xs text-gray-500">
                              {user.email}
                            </span>
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-4 flex justify-center text-center text-gray-500 text-sm">
                        {/* if loading */}
                        {loading ? <Loader /> : "No users found"}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Total Items Input */}
          <div className="mb-4">
            <label
              htmlFor="totalItem"
              className="block text-sm font-medium text-gray-700"
            >
              Items Sold
            </label>
            <input
              type="number"
              id="totalItem"
              name="totalItem"
              value={addSaleFormData.totalItem}
              onChange={(e) => {
                setAddSaleFormData({
                  ...addSaleFormData,
                  totalItem: e.target.value,
                });
              }}
              placeholder="Enter number of items sold"
              className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border p-2"
              min="1"
              required
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              onClick={handleAddTaskChange}
              customClass={"!max-w-fit !mt-0"}
              text={"Submit"}
            />
            <CancelButton
              onClick={() => {
                setAddSaleFormData({
                  user: null,
                  totalItem: "",
                  merchandiseProductId: "",
                });
                setSearchQuery("");
                setSelectUserIsOpen(false);
                setShowAddSalesModal(null);
              }}
              customClass={
                "!max-w-fit !mt-0 bg-gray-300 text-black px-4 py-2 rounded"
              }
              text={"Cancel"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddSalesForm;
